import useResizeObserver from '../../../hooks/useResizeObserver';
import { useRef } from 'react';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';

/**
 * Observes the dimensions of a node and updates the canvas state when they change.
 *
 * @param id - The ID of the node to observe
 * @param width - The initial width of the node
 * @param height - The initial height of the node
 * @returns A ref to the wrapper element of the node
 */
export const useNodeDimensionsObserver = ({
  id,
  width,
  height,
  minWidth = 1,
  minHeight = 1,
}: {
  id: string;
  width: number | undefined;
  height: number | undefined;
  minWidth?: number;
  minHeight?: number;
}) => {
  const { setCanvasNodes } = useSweepCanvasState();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useResizeObserver({
    ref: wrapperRef,
    onResize: ({ width: newWidth = 1, height: newHeight = 1 }) => {
      const _newWidth = Math.max(newWidth, minWidth);
      const _newHeight = Math.max(newHeight, minHeight);

      if (width !== _newWidth || height !== _newHeight) {
        setCanvasNodes((prev) => {
          return prev.map((node) => {
            if (node.id === id) {
              return { ...node, width: _newWidth, height: _newHeight };
            }
            return node;
          });
        });
      }
    },
  });

  return wrapperRef;
};
